import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import api from "../../lib/api";
import type { PackageRow } from "./ModulesTab";

export interface DiscountRow {
  id: string;
  name: string;
  badgeLabel?: string | null;
  type: "PERCENTAGE" | "FIXED";
  value: number | string;
  packageId?: string | null;
  package?: { id: string; name: string } | null;
  companyId?: string | null;
  company?: { id: string; name: string } | null;
  validFrom?: string | null;
  validTo?: string | null;
  isActive: boolean;
  sortOrder: number;
}

interface CompanyOption {
  id: string;
  name: string;
}

interface DiscountsSectionProps {
  packages: PackageRow[];
  flash: (msg: string) => void;
}

type ModalState =
  | { type: "create" }
  | { type: "edit"; discount: DiscountRow }
  | null;

export function DiscountsSection({ packages, flash }: DiscountsSectionProps) {
  const [discounts, setDiscounts] = useState<DiscountRow[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalState>(null);

  const load = async () => {
    try {
      const { data } = await api.get("/api/superadmin/discounts");
      setDiscounts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Bedrijven ophalen voor de per-bedrijf-korting selectie.
    api
      .get("/api/superadmin/companies")
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray((res.data as any)?.companies)
            ? (res.data as any).companies
            : [];
        setCompanies(list.map((c: any) => ({ id: c.id, name: c.name })));
      })
      .catch(() => {});
  }, []);

  const toggleStatus = async (d: DiscountRow) => {
    try {
      await api.patch(`/api/superadmin/discounts/${d.id}/status`, { isActive: !d.isActive });
      await load();
      flash(`Korting ${d.isActive ? "gedeactiveerd" : "geactiveerd"}.`);
    } catch (err: any) {
      flash(err?.response?.data?.error || "Actie mislukt");
    }
  };

  const del = async (d: DiscountRow) => {
    if (!confirm(`Korting "${d.name}" verwijderen?`)) return;
    try {
      await api.delete(`/api/superadmin/discounts/${d.id}`);
      await load();
      flash("Korting verwijderd.");
    } catch (err: any) {
      flash(err?.response?.data?.error || "Verwijderen mislukt");
    }
  };

  const pkgName = (id?: string | null) =>
    id ? packages.find((p) => p.id === id)?.name || "—" : "Alle pakketten";

  const companyName = (id?: string | null) =>
    id ? companies.find((c) => c.id === id)?.name || "—" : "Alle bedrijven";

  const sorted = [...discounts].sort(
    (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.name.localeCompare(b.name)
  );

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Tag size={27} className="text-brand-500" />
          <h3 className="font-bold text-slate-800">Kortingen & acties</h3>
        </div>
        <Button size="sm" onClick={() => setModal({ type: "create" })}>
          <Plus size={24} /> Nieuwe korting
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/80 border-b border-slate-100">
            <tr>
              <th className="text-left px-4 py-3 font-bold text-slate-600">Naam</th>
              <th className="text-left px-4 py-3 font-bold text-slate-600">Badge</th>
              <th className="text-left px-4 py-3 font-bold text-slate-600">Type</th>
              <th className="text-left px-4 py-3 font-bold text-slate-600">Waarde</th>
              <th className="text-left px-4 py-3 font-bold text-slate-600">Geldig voor</th>
              <th className="text-left px-4 py-3 font-bold text-slate-600">Bedrijf</th>
              <th className="text-left px-4 py-3 font-bold text-slate-600">Periode</th>
              <th className="text-left px-4 py-3 font-bold text-slate-600">Status</th>
              <th className="text-right px-4 py-3 font-bold text-slate-600">Acties</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr
                key={d.id}
                className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${
                  !d.isActive ? "opacity-50" : ""
                }`}
              >
                <td className="px-4 py-3 font-semibold text-slate-800">{d.name}</td>
                <td className="px-4 py-3">
                  {d.badgeLabel ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold border border-amber-200">
                      {d.badgeLabel}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge status="default">{d.type === "PERCENTAGE" ? "%" : "€"}</Badge>
                </td>
                <td className="px-4 py-3 font-bold">
                  {d.type === "PERCENTAGE" ? `${Number(d.value)}%` : `€${Number(d.value).toFixed(2)}`}
                </td>
                <td className="px-4 py-3 text-slate-600">{pkgName(d.packageId)}</td>
                <td className="px-4 py-3 text-slate-600">{companyName(d.companyId)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {d.validFrom ? new Date(d.validFrom).toLocaleDateString("nl-NL") : "—"}
                  {" → "}
                  {d.validTo ? new Date(d.validTo).toLocaleDateString("nl-NL") : "∞"}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleStatus(d)}
                    className={`px-2.5 py-0.5 rounded-full text-xs font-bold border transition-colors ${
                      d.isActive
                        ? "bg-emerald-100/80 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-500 border-slate-200"
                    }`}
                  >
                    {d.isActive ? "Actief" : "Inactief"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => setModal({ type: "edit", discount: d })}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-600 transition-colors"
                      title="Bewerken"
                    >
                      <Pencil size={24} />
                    </button>
                    <button
                      onClick={() => del(d)}
                      className="p-2 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                      title="Verwijderen"
                    >
                      <Trash2 size={24} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && sorted.length === 0 && (
        <p className="text-slate-500 text-center py-8">Nog geen kortingen.</p>
      )}

      {modal?.type === "create" && (
        <DiscountFormModal
          packages={packages}
          companies={companies}
          onClose={() => setModal(null)}
          onSaved={async (msg) => {
            await load();
            setModal(null);
            flash(msg);
          }}
        />
      )}
      {modal?.type === "edit" && (
        <DiscountFormModal
          discount={modal.discount}
          packages={packages}
          companies={companies}
          onClose={() => setModal(null)}
          onSaved={async (msg) => {
            await load();
            setModal(null);
            flash(msg);
          }}
        />
      )}
    </Card>
  );
}

interface DiscountFormModalProps {
  discount?: DiscountRow;
  packages: PackageRow[];
  companies: CompanyOption[];
  onClose: () => void;
  onSaved: (msg: string) => Promise<void> | void;
}

function DiscountFormModal({ discount, packages, companies, onClose, onSaved }: DiscountFormModalProps) {
  const isEdit = !!discount;
  const [name, setName] = useState(discount?.name || "");
  const [badgeLabel, setBadgeLabel] = useState(discount?.badgeLabel || "");
  const [type, setType] = useState<"PERCENTAGE" | "FIXED">(discount?.type || "PERCENTAGE");
  const [value, setValue] = useState(discount ? String(discount.value) : "");
  const [packageId, setPackageId] = useState(discount?.packageId || "");
  const [companyId, setCompanyId] = useState(discount?.companyId || "");
  const [validFrom, setValidFrom] = useState(
    discount?.validFrom ? discount.validFrom.slice(0, 10) : ""
  );
  const [validTo, setValidTo] = useState(discount?.validTo ? discount.validTo.slice(0, 10) : "");
  const [isActive, setIsActive] = useState(discount?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState(discount ? String(discount.sortOrder) : "0");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!name.trim()) return setError("Naam is verplicht");
    if (value === "" || Number(value) <= 0) return setError("Waarde moet groter dan 0 zijn");
    if (type === "PERCENTAGE" && Number(value) > 100) return setError("Percentage max 100");

    const body: any = {
      name,
      badgeLabel: badgeLabel || null,
      type,
      value: Number(value),
      packageId: packageId || null,
      companyId: companyId || null,
      validFrom: validFrom ? new Date(validFrom).toISOString() : null,
      validTo: validTo ? new Date(validTo).toISOString() : null,
      isActive,
      sortOrder: Number(sortOrder || 0),
    };

    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/api/superadmin/discounts/${discount!.id}`, body);
        await onSaved("Korting opgeslagen.");
      } else {
        await api.post("/api/superadmin/discounts", body);
        await onSaved("Korting aangemaakt.");
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Opslaan mislukt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? "Korting bewerken" : "Nieuwe korting"}
      size="md"
      footer={
        <div className="flex justify-between items-center">
          {error ? <p className="text-sm text-red-600">{error}</p> : <span />}
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              Annuleren
            </Button>
            <Button variant="primary" onClick={submit} loading={loading}>
              {isEdit ? "Opslaan" : "Aanmaken"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Input label="Naam" value={name} onChange={(e) => setName(e.target.value)} />
        <Input
          label="Badge-label (bijv. Zomeractie)"
          value={badgeLabel}
          onChange={(e) => setBadgeLabel(e.target.value)}
          placeholder="Actie"
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value as any)}
            options={[
              { value: "PERCENTAGE", label: "Percentage (%)" },
              { value: "FIXED", label: "Vast bedrag (€)" },
            ]}
          />
          <Input
            label={type === "PERCENTAGE" ? "Percentage (0-100)" : "Bedrag (€)"}
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
        <Select
          label="Geldig voor pakket"
          value={packageId}
          onChange={(e) => setPackageId(e.target.value)}
          options={[
            { value: "", label: "Alle pakketten" },
            ...packages.map((p) => ({ value: p.id, label: p.name })),
          ]}
        />
        <Select
          label="Geldig voor bedrijf (per-bedrijf korting)"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          options={[
            { value: "", label: "Alle bedrijven" },
            ...companies.map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Geldig vanaf"
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
          />
          <Input
            label="Geldig tot"
            type="date"
            value={validTo}
            onChange={(e) => setValidTo(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Volgorde"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
          <label className="flex items-end gap-2 text-sm font-semibold text-slate-700 pb-3">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-brand-500 focus:ring-brand-500"
            />
            Actief
          </label>
        </div>
      </div>
    </Modal>
  );
}